import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme, neutral, jadeGreen } from '../design-system/theme';
import { TIMEZONES, getUserTimezone, setUserTimezone } from '../utils/dateFormat';

const TimezoneContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TimezoneButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${neutral[1]};
  border: 1px solid ${neutral[3]};
  border-radius: 8px;
  font-size: 13px;
  color: ${theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${neutral[2]};
    border-color: ${jadeGreen[4]};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const TimezoneDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  width: 240px;
  max-height: 320px;
  overflow-y: auto;
  background: white;
  border: 1px solid ${neutral[3]};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const TimezoneOption = styled.button<{ $isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  background: ${props => props.$isSelected ? `${jadeGreen[1]}` : 'transparent'};
  border: none;
  border-bottom: 1px solid ${neutral[2]};
  cursor: pointer;
  transition: background 0.15s;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${props => props.$isSelected ? `${jadeGreen[2]}` : `${neutral[1]}`};
  }

  .timezone-label {
    font-size: 13px;
    color: ${theme.textPrimary};
    font-weight: ${props => props.$isSelected ? '600' : '400'};
  }

  .timezone-offset {
    font-size: 11px;
    color: ${theme.textSubtle};
    font-family: 'SF Mono', monospace;
  }

  .check-icon {
    color: ${jadeGreen[5]};
    font-size: 16px;
    margin-left: 8px;
  }
`;

const DropdownHeader = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${neutral[3]};
  background: ${neutral[1]};
  border-radius: 8px 8px 0 0;

  .title {
    font-size: 12px;
    font-weight: 600;
    color: ${theme.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

interface TimezonePickerProps {
  onChange?: (timezone: string) => void;
  compact?: boolean;
}

export const TimezonePicker: React.FC<TimezonePickerProps> = ({ onChange, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(getUserTimezone());

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-timezone-picker]')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleTimezoneSelect = (timezone: string) => {
    setSelectedTimezone(timezone);
    setUserTimezone(timezone);
    setIsOpen(false);
    
    if (onChange) {
      onChange(timezone);
    }

    // Reload page to apply new timezone to all dates
    window.location.reload();
  };

  const getCurrentTZ = () => {
    const tz = TIMEZONES.find(t => t.value === selectedTimezone);
    return tz || { label: 'Unknown', offset: '' };
  };

  const currentTZ = getCurrentTZ();

  return (
    <TimezoneContainer data-timezone-picker>
      <TimezoneButton onClick={() => setIsOpen(!isOpen)} title="Change timezone">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
        {compact ? currentTZ.label.match(/\(([^)]+)\)/)?.[1] : currentTZ.label}
      </TimezoneButton>

      <TimezoneDropdown $isOpen={isOpen}>
        <DropdownHeader>
          <div className="title">Select Timezone</div>
        </DropdownHeader>
        
        {TIMEZONES.map((tz) => (
          <TimezoneOption
            key={tz.value}
            $isSelected={tz.value === selectedTimezone}
            onClick={() => handleTimezoneSelect(tz.value)}
          >
            <div>
              <div className="timezone-label">{tz.label}</div>
              <div className="timezone-offset">{tz.offset}</div>
            </div>
            {tz.value === selectedTimezone && (
              <span className="check-icon">✓</span>
            )}
          </TimezoneOption>
        ))}
      </TimezoneDropdown>
    </TimezoneContainer>
  );
};
